import styled, { css } from "styled-components";
import { theme } from "../../../styles/theme";
import * as RadixSelect from "@radix-ui/react-select";
import type { SelectSize } from "./types.radix";

const triggerSizes: Record<SelectSize, ReturnType<typeof css>> = {
  sm: css` padding: 10px 38px 10px 14px; font-size: 13px; `,
  md: css` min-height: ${({ theme }) => theme.sizes.controlHeight}; padding: 0 44px 0 18px; font-size: 14px; `,
  lg: css` padding: 16px 48px 16px 18px; font-size: 16px; `,
};

export const Trigger = styled(RadixSelect.Trigger)<{ $size: SelectSize; $hasLeading?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  appearance: none;
  background: ${({ theme }) => theme.colors.senneCream};
  border: 2px solid transparent;
  border-radius: ${({ theme }) => theme.radii.md};
  font-family: ${({ theme }) => theme.typography.fontBody};
  color: ${({ theme }) => theme.colors.fg1};
  cursor: pointer;
  outline: none;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  transition: all ${({ theme }) => theme.motion.durationBase};
  ${({ $size }) => triggerSizes[$size]}
  ${({ $hasLeading }) => $hasLeading && css` padding-left: 48px; `}

  &[data-state="open"], &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.senneWhite};
    box-shadow: ${({ theme }) => theme.shadows.focusRing};
  }

  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

export const LeadSlot = styled.span`
  position: absolute;
  left: 18px;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  color: ${({ theme }) => theme.colors.fg4};
  pointer-events: none;

  ${Trigger}:focus &, ${Trigger}[data-state="open"] & {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

export const ChevronWrap = styled(RadixSelect.Icon)`
  position: absolute;
  right: 18px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.fg4};
  pointer-events: none;
`;

export const Content = styled(RadixSelect.Content)`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.radii.md};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: 6px;
  z-index: ${theme.zIndex.popover};
  overflow: hidden;
  /* The list is as wide as the field and never taller than the room on screen (long hospital lists). */
  min-width: var(--radix-select-trigger-width);
  max-height: min(360px, var(--radix-select-content-available-height));
`;

export const Viewport = styled(RadixSelect.Viewport)`
  padding: 4px;
  max-height: inherit;
  overflow-y: auto;
`;

export const Item = styled(RadixSelect.Item)`
  display: flex;
  align-items: center;
  padding: 10px 14px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.fg1};
  border-radius: ${({ theme }) => theme.radii.sm};
  cursor: pointer;
  outline: none;

  &[data-highlighted] {
    background: ${({ theme }) => theme.colors.tintOrange50};
    color: ${({ theme }) => theme.colors.primary};
  }
  &[data-state="checked"] {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: ${({ theme }) => theme.typography.fw.bold};
  }
  &[data-disabled] { color: ${({ theme }) => theme.colors.fg4}; cursor: not-allowed; }
`;
